import React, { useState, useEffect } from "react";
import { CSSColor } from "@frontend/app/constants/colors.ts";
import PlaneIcon from "@frontend/app/assets/icons/PlaneIcon.tsx";
import InfoExIcon from "@frontend/app/assets/icons/InfoExIcon.tsx";
import AmpBridgeIcon from "@frontend/app/assets/icons/AmpBridgeIcon.tsx";
import VezaIcon from "@frontend/app/assets/icons/VezaIcon.tsx";
import FadeUp from "@frontend/app/anims/FadeUp.tsx";
import { default as ProjectComponent } from "./Project.tsx";
import AIELogo from "@frontend/app/assets/icons/AIEIcon.tsx";
import { SiAmazon } from "react-icons/si";
import clsx from "clsx";

type Project = {
  title: string;
  company: string;
  position: string;
  Logo: React.ElementType;
  descriptionPoints: string[];
  accentColor: CSSColor;
  nda: boolean;
  subtitle?: string;
};

const PROJECTS: readonly Project[] = Object.freeze([
  {
    title: "Veza",
    company: "Veza",
    position: "Engineering Lead, Frontend",
    Logo: VezaIcon,
    description:
      "Artificial intelligence platform providing customizable agents and workflows with a focus on the software development lifecycle.",
    descriptionPoints: [
      "Led the frontend team, ensuring that the product was delivered on time and within budget.",
      "Worked closely with the product team to ensure that the product was delivered on time and within budget.",
      "Worked closely with the product team to ensure that the product was delivered on time and within budget.",
    ],
    nda: false,
    accentColor: "#7145E9" as CSSColor,
  },
  {
    title: "AmpBridge",
    company: "AmpBridge",
    position: "Sole Developer",
    Logo: AmpBridgeIcon,
    description:
      "A server application with UI for controlling audio systems that use serial communication. Written and designed to control ELAN amplifiers, but should work in theory with any serial-based audio system. The app also sends MQTT messages to Home Assistant and can be controlled via the Home Assistant integration.",
    descriptionPoints: [
      "Built a bidirectional serial relay system that learns commands from existing controllers and enables modern control interfaces.",
      "Developed a real-time web interface using Phoenix LiveView with multi-user synchronization for volume control and zone management.",
      "Implemented command learning system that captures and replays controller commands, eliminating manual configuration.",
      "Created cable wiring helper UI that generates wiring diagrams for ELAN's non-standard pinouts.",
      "Integrated MQTT bridge for Home Assistant, enabling smart home automation and control.",
      "Built with Elixir and Erlang/OTP (Open Telecom Platform) for fault-tolerant backend architecture with process supervision.",
    ],
    nda: false,
    accentColor: "#FFFFFF" as CSSColor,
  },
  {
    title: "AI Expedite",
    company: "AI Expedite",
    position: "Staff Front End Engineer",
    Logo: AIELogo,
    description:
      "Artificial intelligence platform providing customizable agents and workflows with a focus on the software development lifecycle.",
    descriptionPoints: [
      "Primary scrum master for standup, sprint planning, and sprint reviews.",
      "Provided mentorship and guidance to other engineers, ensuring they were able to meet their goals and deadlines.",
      "Enforced higher standards of code quality, documentation and test driven development.",
    ],
    nda: false,
    accentColor: "#FFFFFF" as CSSColor,
  },
  {
    title: "Amazon",
    company: "Amazon",
    position: "Front End Engineer",
    Logo: SiAmazon,
    description:
      "An internal application used for engaging with customers internationally while ensuring timely and accurate delivery of products.",
    descriptionPoints: [
      "Led multiple features including a system for internal customers to generate and serve content externally while collecting data from the customers. This was crucial for day to day operations and the older version of this feature was hard to maintain and use.",
      "The new system I built out employed a schema based approach, providing an intuitive and cohesive system for developers and normal users. I consistently maintained communication with our internal customers and iteratively made a product that got widespread approval and praise. We saw a significant bump in usage while receiving a fraction of the tickets we did with the old system.",
      "Increased efficiency and reducing bugs by introducing standardization and automation.",
      "Consistently led teaching sessions in excess of 30 attendees, covering React, Redux, Context, Higher-Order Components, and more.",
      "Led a customer-centric feature that overhauled significant portions of the application.",
    ],
    nda: true,
    accentColor: "#FF9901" as CSSColor,
  },
  {
    title: "InfoEx",
    company: "Canadian Avalanche Association",
    position: "Full Stack Developer",
    Logo: InfoExIcon,
    description:
      "A web application that provides avalanche professionals with a platform to share and access critical information. The application is used by avalanche professionals world-wide to share and access critical information.",
    descriptionPoints: [
      "Introduced new microservices to replace legacy systems, increasing performance and reducing costs while strengthening security.",
      "Increased performance to support users that often had a slow cellular connection.",
      "Worked closely with designers, project managers and the client to ensure the application captured business requirements.",
      "was user friendly and accessible through our new component library.",
    ],
    nda: false,
    accentColor: "#26ace2" as CSSColor,
  },
  {
    title: "MagTable",
    company: "Aeromag",
    position: "Front End Developer",
    Logo: PlaneIcon,
    description: "Web application for managing inventory, staff, and operations.",
    descriptionPoints: [
      "SAIT capstone project.",
      "Used at the Calgary International Airport for de-icing operations.",
      "Trusted for the safe travel of over 18 million passengers annually.",
    ],
    nda: false,
    accentColor: "#FFFFFF" as CSSColor,
    subtitle: "Inventory, staff and operations management system.",
  },
]);

const ProjectsPage = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return (
    <div>
      <FadeUp mounted={mounted}>
        <h1 className="mt-10">Projects</h1>
      </FadeUp>
      <div className="gap-8 max-w-screen-xl mx-auto py-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        <FadeUp>
          {PROJECTS.map((project, index) => (
            <ProjectComponent
              mounted={mounted}
              key={project.title}
              delay={index * 100}
              {...project}
            />
          ))}
        </FadeUp>
      </div>
    </div>
  );
};

export default ProjectsPage;
